/**
 * `notifications.*` — company notification preferences and in-app feed.
 */
import { z } from "zod";
import { uuid } from "../_common.js";

export const notificationChannel = z.enum(["whatsapp", "email", "sms", "in-app"]);
export const externalChannel = z.enum(["whatsapp", "email", "sms"]);
export const fallbackChannel = z.enum(["whatsapp", "email", "sms", "none"]);

export const eventKey = z.enum([
  "invoice.issued",
  "purchase-order.sent",
  "payment.received",
  "approval.requested",
  "payment.reminder",
  "compliance.deadline",
  "security.alert",
  "system.alert",
  // Storefront orders (ADR-0064). Byte-identical to `TEMPLATE_KEYS` in
  // `@flint/domain/notifications/types` — the bus resolves the preference row
  // by `templateKey` while this enum is what the preferences UI writes, so the
  // two only meet when the strings match exactly.
  "store-order.placed",
  "store-order.status-changed",
  "store-order.rejected",
]);

export const eventPreference = z.object({
  eventKey,
  label: z.string(),
  description: z.string(),
  severity: z.enum(["critical", "important", "informational"]),
  supportsReminders: z.boolean(),
  enabled: z.boolean(),
  whatsappEnabled: z.boolean(),
  emailEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  primaryChannel: externalChannel,
  fallbackChannel,
  firstReminderAfterDays: z.number().int().min(0).max(90),
  repeatEveryDays: z.number().int().min(0).max(90),
  maxReminderCount: z.number().int().min(0).max(12),
});

export const getPreferencesInput = z.object({});
export const getPreferencesOutput = z.object({
  masterEnabled: z.boolean(),
  events: z.array(eventPreference),
});

export const updatePreferencesInput = z.object({
  masterEnabled: z.boolean().optional(),
  events: z.array(eventPreference.omit({ label: true, description: true, severity: true, supportsReminders: true })).min(1).optional(),
});
export const updatePreferencesOutput = z.object({
  ok: z.boolean(),
  changedCount: z.number().int().nonnegative(),
});

export const listInput = z.object({
  limit: z.number().int().min(1).max(30).default(10),
});
export const notificationItem = z.object({
  id: uuid,
  channel: notificationChannel,
  kind: z.enum(["otp", "transactional", "system-alert", "ai-reply"]),
  status: z.enum(["queued", "held", "sending", "sent", "failed"]),
  recipientType: z.enum(["user", "customer", "vendor", "raw"]),
  recipientHandle: z.string(),
  templateKey: z.string(),
  subject: z.string(),
  detail: z.string(),
  queuedAt: z.string(),
  sentAt: z.string().nullable(),
  holdReason: z.string().nullable(),
  errorMessage: z.string().nullable(),
  read: z.boolean(),
});
export const listOutput = z.object({
  items: z.array(notificationItem),
  unreadCount: z.number().int().nonnegative(),
});

export const markReadInput = z.object({
  ids: z.array(uuid).min(1).max(50),
});
export const markReadOutput = z.object({ ok: z.boolean() });

export type NotificationEventPreferenceWire = z.infer<typeof eventPreference>;
